import React, { useState, useEffect, useRef } from 'react';

// --- Flying Bat ---
export const FlyingBat = ({ delay = 0, startX = -50, startY = 80, size = 24 }) => {
  const [pos, setPos] = useState({ x: startX, y: startY });
  const frame = useRef(0);
  const baseX = useRef(startX);

  useEffect(() => {
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        frame.current += 0.02;
        const newX = baseX.current + Math.sin(frame.current * 1.5) * 120 + frame.current * 15;
        const newY = startY + Math.cos(frame.current * 2) * 40;
        if (newX > window.innerWidth + 100) {
          baseX.current = -100;
          frame.current = 0;
        }
        setPos({ x: newX, y: newY });
      }, 30);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay, startY]);

  return (
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y,
      fontSize: size, zIndex: 5, pointerEvents: 'none',
      transition: 'left 0.03s linear, top 0.03s linear',
      filter: 'drop-shadow(0 0 8px rgba(189,147,249,0.4))',
    }}>
      🦇
    </div>
  );
};

// --- Code Rain Background ---
export const CodeRain = () => {
  const lines = [
    "const dracula = new Developer();",
    "while(true) { code(); }",
    "import { creativity } from 'transylvania';",
    "function buildWebsite(idea) {",
    "  return <MasterPiece />;",
    "async fetchClients() => await magic();",
    "git commit -m 'another happy client'",
    "npm install dracula-powers",
    "export default DraculaDev;",
    "const [success, setSuccess] = useState(true);",
    "// TODO: sleep is for the living",
    "class DraculaDev extends Immortal {",
    "<DraculaDev clients={infinity} />",
    "docker run --name castel transylvania",
    "ssh dracula@carpathian-mountains.dev",
  ];

  const colors = ['#50fa7b', '#ff79c6', '#bd93f9', '#8be9fd', '#ffb86c'];

  return (
    <div style={{
      position: 'fixed', inset: 0, overflow: 'hidden',
      pointerEvents: 'none', zIndex: 0, opacity: 0.07,
    }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          position: 'absolute',
          left: `${(i * 7) % 90}%`,
          top: `${(i * 13) % 100}%`,
          fontFamily: "'Fira Code', monospace",
          fontSize: `${10 + (i % 4) * 2}px`,
          color: colors[i % 5],
          whiteSpace: 'nowrap',
          animation: `floatCode ${15 + i * 2}s linear infinite`,
          animationDelay: `${i * 0.8}s`,
          transform: `rotate(${-5 + (i % 3) * 5}deg)`,
        }}>
          {line}
        </div>
      ))}
    </div>
  );
};

// --- Magic Particles (Kids Section) ---
export const MagicParticles = () => {
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 5,
    duration: 3 + Math.random() * 4,
    size: 4 + Math.random() * 8,
    emoji: ['✨', '⭐', '🌙', '💜', '🔮', '🕸️'][i % 6],
  }));

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1, overflow: 'hidden' }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          left: `${p.left}%`,
          bottom: '-20px',
          fontSize: `${p.size}px`,
          animation: `riseParticle ${p.duration}s ease-in-out infinite`,
          animationDelay: `${p.delay}s`,
          opacity: 0,
        }}>
          {p.emoji}
        </div>
      ))}
    </div>
  );
};

// --- Castle Silhouette ---
export const CastleSilhouette = ({ color = '#1a1a2e' }) => (
  <svg viewBox="0 0 800 200" style={{ width: '100%', display: 'block' }} preserveAspectRatio="none">
    <path d={`
      M0,200 L0,160 L30,160 L30,140 L40,140 L40,120 L50,120 L50,140 L60,140 L60,160
      L100,160 L100,100 L110,100 L110,80 L120,70 L130,80 L130,100 L140,100 L140,160
      L200,160 L200,130 L210,130 L210,110 L220,110 L220,90 L230,80 L240,90 L240,110 L250,110 L250,130 L260,130 L260,160
      L320,160 L320,60 L330,60 L330,40 L340,30 L350,20 L360,30 L370,40 L370,60 L380,60 L380,160
      L440,160 L440,120 L450,120 L450,100 L460,90 L470,100 L470,120 L480,120 L480,160
      L540,160 L540,80 L550,80 L550,50 L560,40 L570,30 L580,40 L580,50 L590,50 L590,80 L600,80 L600,160
      L660,160 L660,130 L670,130 L670,110 L680,100 L690,110 L690,130 L700,130 L700,160
      L760,160 L760,140 L770,140 L770,120 L780,120 L780,140 L790,140 L790,160
      L800,160 L800,200 Z
    `} fill={color} />
    <circle cx="345" cy="55" r="4" fill="#f1fa8c" opacity="0.8" />
    <circle cx="365" cy="50" r="3" fill="#f1fa8c" opacity="0.6" />
    <circle cx="565" cy="60" r="4" fill="#f1fa8c" opacity="0.7" />
    <rect x="350" y="65" width="8" height="12" rx="4" fill="#ffb86c" opacity="0.4" />
    <rect x="562" y="68" width="8" height="12" rx="4" fill="#ffb86c" opacity="0.4" />
  </svg>
);

// --- Night Sky (Kids) ---
export const NightSky = () => (
  <>
    {/* Moon */}
    <div style={{
      position: 'fixed', right: '8%', top: '5%',
      width: 100, height: 100, borderRadius: '50%',
      background: 'radial-gradient(circle, #f1fa8c 0%, #f1fa8c 40%, rgba(241,250,140,0.3) 70%, transparent 100%)',
      boxShadow: '0 0 60px rgba(241,250,140,0.4), 0 0 120px rgba(241,250,140,0.15)',
      zIndex: 2, pointerEvents: 'none',
    }} />
    {/* Stars */}
    {Array.from({ length: 20 }, (_, i) => (
      <div key={i} style={{
        position: 'fixed',
        left: `${5 + (i * 4.7) % 90}%`,
        top: `${3 + (i * 7.3) % 35}%`,
        width: 2 + (i % 3), height: 2 + (i % 3),
        background: '#f8f8f2', borderRadius: '50%',
        opacity: 0.3 + (i % 4) * 0.15,
        animation: `twinkle ${2 + (i % 3)}s ease-in-out infinite`,
        animationDelay: `${i * 0.3}s`,
        zIndex: 1, pointerEvents: 'none',
      }} />
    ))}
  </>
);
